// Phase 8 R4 — toggle a canned job's shop-level favorite state.
// Optimistic update across both R1 selector cache and R2 admin cache.

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

interface Vars {
  cannedJobId: string;
  shopId: string;
  isFavorite: boolean;
}

export function useToggleCannedJobFavorite() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ cannedJobId, isFavorite }: Vars) => {
      const { data, error } = await (supabase as any).rpc('set_canned_job_favorite', {
        p_canned_job_id: cannedJobId,
        p_is_favorite: isFavorite,
        p_favorite_order: null,
      });
      if (error) throw error;
      return data;
    },
    onMutate: async ({ cannedJobId, shopId, isFavorite }) => {
      await qc.cancelQueries({ queryKey: ['canned-jobs', shopId] });
      await qc.cancelQueries({ queryKey: ['canned-jobs-admin', shopId] });

      // Optimistically update every matching cached query.
      const patch = (rows: any[] | undefined) =>
        Array.isArray(rows)
          ? rows.map((r) =>
              r.id === cannedJobId ? { ...r, is_favorite: isFavorite } : r,
            )
          : rows;

      qc.getQueriesData({ queryKey: ['canned-jobs', shopId] }).forEach(([key, data]) => {
        qc.setQueryData(key, patch(data as any[]));
      });
      qc.getQueriesData({ queryKey: ['canned-jobs-admin', shopId] }).forEach(([key, data]) => {
        qc.setQueryData(key, patch(data as any[]));
      });
    },
    onError: (err: any, _vars, _ctx) => {
      // Re-fetch on error to restore truth.
      qc.invalidateQueries({ queryKey: ['canned-jobs'] });
      qc.invalidateQueries({ queryKey: ['canned-jobs-admin'] });
      toast({
        title: 'Could not update favorite',
        description: err?.message ?? 'Unknown error',
        variant: 'destructive',
      });
    },
    onSettled: (_data, _err, { shopId }) => {
      // Soft-refresh in the background so favorite_order is accurate.
      qc.invalidateQueries({ queryKey: ['canned-jobs', shopId] });
      qc.invalidateQueries({ queryKey: ['canned-jobs-admin', shopId] });
    },
  });
}
